import { sha256 } from 'hash-wasm'

/** 邀请码允许使用的 Crockford Base32 字符集。 */
export const InviteAlphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/** 规范化用户输入的邀请码。 */
export function normalizeInviteCode(value: string): string {
  return value.trim().toUpperCase()
}

/**
 * 校验 MG 邀请码的长度、字符集和 SHA-256 校验位。
 *
 * ⚠️ 【禁止在本文件用 ES2022+ 的实例方法】(#13 / #16 / #19)
 * 本函数跑在用户手机的浏览器里，机型跨度极大。`vite` 的 `build.target` 只降级**语法**，
 * **不 polyfill 实例方法** —— 例如 `String.prototype.at`（ES2022，Chrome 92+）会原样进产物。
 * 中兴 S30 是 Chrome 78，`code.at` 为 `undefined` → 抛 TypeError → `readOfficialInviteCode`
 * 整个 reject → 页面落到 `{ kind: 'home' }`，用户看到的是**蜜罐首页而不是任何错误提示**，
 * 与「链接没带邀请码」完全无法区分。已用 e2e 复现（删掉 String.prototype.at 即回到首页）。
 * 所以这里改用下标取末位；后续维护也请只用 ES2015 及更早的实例方法。
 */
export async function isCanonicalInviteCode(value: string): Promise<boolean> {
  const code = normalizeInviteCode(value)
  if (!/^MG[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{11}$/.test(code)) {
    return false
  }

  const body = code.slice(0, -1)
  const digest = await sha256(body)
  const checksum = InviteAlphabet[Number.parseInt(digest.slice(0, 2), 16) >> 3]
  return code[code.length - 1] === checksum
}

/** 标准邀请码的格式（不含校验位判断），大小写不敏感。 */
const StandardInvitePattern = /^MG[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{11}$/

/** 自定义邀请码规范化后的格式（需求 5006346）。 */
const CustomInvitePattern = /^[a-z0-9_-]{4,32}$/

/**
 * 自定义邀请码保留词，与服务端默认列表一致。服务端可以再扩展，扩展部分由服务端拒绝。
 * 这里只是本地拦截明显不可能有效的值，省一次请求。
 */
export const CustomInviteReservedWords = [
  'mg', 'install', 'invite', 'admin', 'api', 'null', 'undefined', 'none', 'system', 'test',
  'dev', 'prod', 'support', 'help', 'download', 'resolve', 'status', 'current',
]

/** 自定义邀请码规范化：只清首尾空白并转小写。 */
export function normalizeCustomInviteCode(value: string): string {
  return value.trim().toLowerCase()
}

/** 是否长得像标准邀请码（MG + 11 位 Crockford Base32），不看校验位。 */
export function hasStandardInviteFormat(value: string): boolean {
  return StandardInvitePattern.test(normalizeInviteCode(value))
}

/**
 * 是否按自定义邀请码处理。
 *
 * 【标准码判定优先】长得像标准码的一律走标准码逻辑，校验位不对也不改判为自定义码，
 * 否则抄错一位的标准码会被当成别名发给后端，本地「格式不正确」的提示（#32）就没了。
 * 与 SDK（honeypot-launch.js 的 isCustomInviteCode）保持同一口径。
 *
 * 这里只判断格式与保留词，**不判断别名是否存在、是否启用**——那是后端的裁决。
 */
export function isCustomInviteCode(value: string): boolean {
  if (hasStandardInviteFormat(value)) return false
  const code = normalizeCustomInviteCode(value)
  return CustomInvitePattern.test(code) && CustomInviteReservedWords.indexOf(code) < 0
}

/** 邀请码的规范值：标准码转大写、自定义码转小写；两种都不是返回空串。 */
export function canonicalizeInviteCode(value: string): string {
  if (hasStandardInviteFormat(value)) return normalizeInviteCode(value)
  return isCustomInviteCode(value) ? normalizeCustomInviteCode(value) : ''
}
